'use client'

/**
 * A number that ticks.
 *
 * One component for every live figure on the site: a bitcoin price in the
 * footer, a market cap in a collection header, a balance in the wallet. It
 * takes a number and a formatter, and when the number changes it glides from
 * the old value to the new one over a short ease-out and tints the text with
 * the direction of the move for a beat, then settles back.
 *
 * The glide is a transform-free rAF loop over the formatter; nothing here
 * animates layout. Under `prefers-reduced-motion` the value snaps and only
 * the tint plays, which is a colour transition and no motion at all.
 */

import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

const GLIDE_MS = 260
const TINT_MS = 700

/** ease-out-quart */
function ease(t: number): number {
	return 1 - Math.pow(1 - t, 4)
}

function reducedMotion(): boolean {
	return (
		typeof window !== 'undefined' &&
		typeof window.matchMedia === 'function' &&
		window.matchMedia('(prefers-reduced-motion: reduce)').matches
	)
}

export interface TickerProps {
	/** The live value. `null` renders the placeholder without animating. */
	value: number | null | undefined
	format: (value: number) => React.ReactNode
	/** What to show before the first value. */
	placeholder?: React.ReactNode
	/** Tint direction on change: default up = success, down = loss. */
	tint?: boolean
	className?: string
	/** Below this relative change the glide is skipped and the value snaps. */
	snapBelow?: number
}

export default function Ticker({
	value,
	format,
	placeholder = '—',
	tint = true,
	className,
	snapBelow = 0
}: TickerProps) {
	const [shown, setShown] = useState<number | null>(value ?? null)
	const [direction, setDirection] = useState<'up' | 'down' | null>(null)
	const shownRef = useRef<number | null>(value ?? null)
	const frameRef = useRef<number | null>(null)
	const tintTimerRef = useRef<number | null>(null)

	useEffect(() => {
		if (value == null || !Number.isFinite(value)) {
			return
		}
		const from = shownRef.current
		if (from === null || from === value) {
			shownRef.current = value
			setShown(value)
			return
		}
		if (tint) {
			setDirection(value > from ? 'up' : 'down')
			if (tintTimerRef.current !== null) {
				window.clearTimeout(tintTimerRef.current)
			}
			tintTimerRef.current = window.setTimeout(() => {
				tintTimerRef.current = null
				setDirection(null)
			}, TINT_MS)
		}
		const tiny = from !== 0 && Math.abs(value - from) / Math.abs(from) < snapBelow
		if (reducedMotion() || tiny) {
			shownRef.current = value
			setShown(value)
			return
		}
		if (frameRef.current !== null) {
			cancelAnimationFrame(frameRef.current)
		}
		const start = performance.now()
		const step = (now: number) => {
			const t = Math.min(1, (now - start) / GLIDE_MS)
			const next = from + (value - from) * ease(t)
			shownRef.current = next
			setShown(next)
			if (t < 1) {
				frameRef.current = requestAnimationFrame(step)
			} else {
				frameRef.current = null
				shownRef.current = value
				setShown(value)
			}
		}
		frameRef.current = requestAnimationFrame(step)
		return () => {
			if (frameRef.current !== null) {
				cancelAnimationFrame(frameRef.current)
				frameRef.current = null
			}
		}
	}, [value, tint, snapBelow])

	useEffect(
		() => () => {
			if (tintTimerRef.current !== null) {
				window.clearTimeout(tintTimerRef.current)
			}
		},
		[]
	)

	return (
		<span
			className={classNames(
				'tabular-nums transition-colors duration-700 ease-out',
				direction === 'up' && 'text-success',
				direction === 'down' && 'text-error-400',
				className
			)}
			data-direction={direction ?? undefined}
		>
			{shown === null ? placeholder : format(shown)}
		</span>
	)
}
